import { supabase } from './supabase';

export interface SubscriptionInfo {
  plan: string;
  planStatus: string;
  subscriptionStatus: string;
  subscriptionId: string | null;
  trialEndDate: string | null;
  subscriptionEndDate: string | null;
  isActive: boolean;
  isTrialActive: boolean;
  isSubscriptionActive: boolean;
}

export const checkSubscriptionStatus = async (userId: string): Promise<SubscriptionInfo | null> => {
  try {
    console.log('🔍 [SUBSCRIPTION_UTILITY] Starting subscription check for user:', userId);
    
    const { data: userData, error } = await supabase
      .from('users')
      .select('plan, plan_status, subscription_status, subscription_id, trial_end_date, subscription_end_date')
      .eq('id', userId)
      .single();

    console.log('📊 [SUBSCRIPTION_UTILITY] Database query result:', {
      userData,
      error,
      hasData: !!userData
    });

    if (error || !userData) {
      console.error('❌ [SUBSCRIPTION_UTILITY] Error fetching user data:', error);
      return null;
    }

    console.log('📋 [SUBSCRIPTION_UTILITY] User data from database:', {
      plan: userData.plan,
      plan_status: userData.plan_status,
      subscription_status: userData.subscription_status,
      subscription_id: userData.subscription_id,
      trial_end_date: userData.trial_end_date,
      subscription_end_date: userData.subscription_end_date
    });

    // Check if trial is still active
    const trialEndDate = userData.trial_end_date;
    const now = new Date();
    const isTrialActive = userData.plan_status === 'trial' && 
      trialEndDate && 
      new Date(trialEndDate) > now;

    console.log('🔍 [SUBSCRIPTION_UTILITY] Trial check:', {
      plan_status: userData.plan_status,
      trial_end_date: trialEndDate,
      now: now.toISOString(),
      trial_end_date_parsed: trialEndDate ? new Date(trialEndDate).toISOString() : null,
      is_trial_active: isTrialActive
    });

    // Check if subscription is active
    const isSubscriptionActive = userData.subscription_status === 'active' || 
      userData.subscription_status === 'trialing';

    console.log('🔍 [SUBSCRIPTION_UTILITY] Subscription check:', {
      subscription_status: userData.subscription_status,
      is_subscription_active: isSubscriptionActive
    });

    const isActive = isTrialActive || isSubscriptionActive;

    const result = {
      plan: userData.plan,
      planStatus: userData.plan_status,
      subscriptionStatus: userData.subscription_status,
      subscriptionId: userData.subscription_id,
      trialEndDate: userData.trial_end_date,
      subscriptionEndDate: userData.subscription_end_date,
      isActive,
      isTrialActive,
      isSubscriptionActive
    };

    console.log('✅ [SUBSCRIPTION_UTILITY] Final subscription result:', result);
    return result;
  } catch (error) {
    console.error('❌ [SUBSCRIPTION_UTILITY] Error checking subscription status:', error);
    return null;
  }
};

export const hasActiveSubscription = async (userId: string): Promise<boolean> => {
  const subscriptionInfo = await checkSubscriptionStatus(userId);
  return subscriptionInfo?.isActive || false;
};

export const getTrialDaysRemaining = (trialEndDate: string | null): number => {
  if (!trialEndDate) return 0;
  
  const endDate = new Date(trialEndDate);
  const now = new Date();
  const diffTime = endDate.getTime() - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  return Math.max(0, diffDays);
};

export const formatSubscriptionStatus = (status: string): string => {
  const statusMap: { [key: string]: string } = {
    'active': 'Activa',
    'trialing': 'Período de Prueba',
    'past_due': 'Pago Pendiente',
    'canceled': 'Cancelada',
    'unpaid': 'Sin Pago',
    'incomplete': 'Incompleta',
    'incomplete_expired': 'Expirada',
    'inactive': 'Inactiva',
    'trial': 'Prueba'
  };
  
  return statusMap[status] || status;
};

export const getSubscriptionColor = (status: string): string => {
  const colorMap: { [key: string]: string } = {
    'active': 'text-green-600 bg-green-100',
    'trialing': 'text-blue-600 bg-blue-100',
    'past_due': 'text-yellow-600 bg-yellow-100',
    'canceled': 'text-red-600 bg-red-100',
    'unpaid': 'text-red-600 bg-red-100',
    'incomplete': 'text-orange-600 bg-orange-100',
    'incomplete_expired': 'text-gray-600 bg-gray-100',
    'inactive': 'text-gray-600 bg-gray-100',
    'trial': 'text-blue-600 bg-blue-100'
  };
  
  return colorMap[status] || 'text-gray-600 bg-gray-100';
};

export const isTrialEndingSoon = (trialEndDate: string | null, daysThreshold: number = 4): boolean => {
  if (!trialEndDate) return false;
  
  const daysRemaining = getTrialDaysRemaining(trialEndDate);
  return daysRemaining <= daysThreshold && daysRemaining > 0;
};
